import { UserIcon } from "app/components/Icons";
import CheckListProgress from "app/containers/Features/components/CheckListProgress";
import circleCheckedIcon from "assets/svgs/circleCheckedIcon.svg";
import circleIcon from "assets/svgs/circleIcon.svg";
import historyIcon from "assets/svgs/ic_checklist.svg";
import React, { useEffect, useMemo, useRef, useState } from "react";
import CustomPopover from "./CustomPopover";
import { ITickets } from "../../../Tickets/types";
import type { IActionItem } from "app/containers/Features/containers/Ticket/types";

interface CheckListProps {
  data: ITickets;
  status: string;
}

const HOVER_CLOSE_DELAY = 120;

const CheckList: React.FC<CheckListProps> = (props) => {
  const { data, status } = props;
  void status;
  const [open, setOpen] = useState(false);
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearCloseTimeout = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  };

  const handleHoverStart = () => {
    clearCloseTimeout();
    setOpen(true);
  };

  const handleHoverEnd = () => {
    clearCloseTimeout();
    closeTimeoutRef.current = setTimeout(() => {
      setOpen(false);
    }, HOVER_CLOSE_DELAY);
  };

  useEffect(() => () => clearCloseTimeout(), []);

  const items = useMemo(
    () =>
      (data?.actionItems ?? []).filter(
        (item): item is IActionItem =>
          item != null &&
          typeof item === "object" &&
          typeof (item as IActionItem)._id === "string",
      ),
    [data?.actionItems],
  );

  const { completedCount, total } = useMemo(() => {
    const completed = items.filter((item) => Boolean(item.completed)).length;
    return { completedCount: completed, total: items.length };
  }, [items]);

  return (
    <React.Fragment>
      <CustomPopover
        show={open}
        onClose={(val) => setOpen(val)}
        trigger={
          <div
            className="flex flex-row items-center rounded px-1 py-[1px] hover:bg-[#ECEEF2]"
            onMouseEnter={handleHoverStart}
            onMouseLeave={handleHoverEnd}
          >
            <div className="min-w-[16px]">
              <img src={historyIcon} alt="task-icon" className="size-[16px]" />
            </div>
            <div className="text-xs leading-[18px] text-secondary font-normal mt-[2px] ml-[2px]">{`${completedCount}/${total}`}</div>
          </div>
        }
        content={
          <div
            className="flex flex-col"
            onMouseEnter={handleHoverStart}
            onMouseLeave={handleHoverEnd}
          >
            <div className="sticky top-0 bg-white pt-4 flex gap-2 items-center">
              <p className="text-md font-medium text-[#253858]">Checklists</p>
              <CheckListProgress
                progress={
                  total === 0 ? 0 : (Number(completedCount) / Number(total)) * 100
                }
              />
              <p className="text-xs leading-[21px] font-medium text-black">
                {`${completedCount}/${total}`}
              </p>
            </div>
            <div className="border border-gray-100 rounded-t-[8px] bg-gray-400 flex gap-2 items-center h-12 p-4 mt-4">
              <p className="text-md font-medium text-[#253858]">Checklists</p>
              <p className="text-xs leading-[21px] font-medium text-black">
                {`${completedCount}/${total}`}
              </p>
            </div>
            <ul className="m-0 list-none p-0" aria-label="Checklist preview">
              {items.map((ele) => (
                <li
                  key={ele._id}
                  className="flex h-[48px] items-center justify-between gap-2 border-b border-gray-100 ps-4 pe-2 last:border-b-0"
                >
                  <div className="flex min-w-0 items-center gap-2">
                    <span className="flex shrink-0 items-center" aria-hidden>
                      {ele.completed ? (
                        <img
                          src={circleCheckedIcon}
                          alt=""
                          sizes="20"
                        />
                      ) : (
                        <img src={circleIcon} alt="" sizes="20" />
                      )}
                    </span>
                    <span className="ml-1 overflow-hidden text-ellipsis text-nowrap">
                      {ele.title}
                    </span>
                  </div>
                  <span className="shrink-0 text-secondary" aria-hidden>
                    <UserIcon />
                  </span>
                </li>
              ))}
            </ul>
          </div>
        }
        className="max-h-[300px] !pt-0"
      />
    </React.Fragment>
  );
};

export default CheckList;
